/* eslint-disable @typescript-eslint/no-explicit-any */
export type PixelState = {
  isLoading: boolean;
  error: string | null;
  widget?: PixelWidget | null;
  events?: PixelEvents | null;
  categories?: PixelEventCategory[] | null;
  eventsDaily?: PixelEventDaily[] | null;
};

export type PixelRequest = {
  advertiserId: string;
  startDateTime: string;
  endDateTime: string;
  category?: string | null;
  subcategory?: string | null;
};

export type PixelWidget = {
  eventsFiredToday: number;
  averageDailyEvents: number;
  totalEventsFired: number;
};

export type PixelEventsRequest = {
  advertiserId: string;
  startDateTime: string;
  endDateTime: string;
  page: number;
  size: number;
  category?: string | null;
  subcategory?: string | null;
};

export type PixelEvents = {
  data: PixelEvent[];
  totalPages: number;
  totalCount: number;
};

export type PixelEvent = {
  timestamp: string;
  orderNumber: string;
  checkoutValue: number;
  category: string;
  subcategory: string;
  referrerUrl: string;
};

export type PixelEventCategory = {
  category: string;
  totalEvents: number;
  subcategories: PixelEventSubcategory[];
};

export type PixelEventSubcategory = {
  subcategory: string;
  totalEvents: number;
};

export type PixelEventDaily = {
  date: string;
  events: number;
};

// Pixel Config Types (matching backend DTOs)

export type PixelConfigDTO = {
  pixelId: string;
  version: number | null;
  trackPageView: boolean | null;
  conversions: ConversionRule[] | null;
  dspMappings: Record<string, string> | null;
};

export type ConversionRule = {
  name: string;
  category: string;
  subcategory?: string | null;
  trigger: TriggerConfig[];
  extractors?: Record<string, ExtractorConfig> | null;
  frequency?: FrequencyType | null;
  // Per-rule DSP destinations (e.g. meta, google); read by the S2S installation snippet
  dspMappings?: Record<string, string> | null;
};

// Trigger Types:
// - page_load: fire on init
// - url_match: regex against location.pathname
// - url_contains: string match against location.href
// - click: event delegation for CSS selector
// - form_submit: event delegation for CSS selector
export type TriggerType = 'page_load' | 'url_match' | 'url_contains' | 'click' | 'form_submit';

export type TriggerConfig = {
  type: TriggerType;
  pattern?: string | null; // For url_match (regex)
  selector?: string | null; // For click, form_submit (CSS selector)
  contains?: string | null; // For url_contains (string)
};

// Extractor Types:
// - urlParam: from URLSearchParams
// - selector: textContent or value from DOM element
// - attribute: data-* from clicked/submitted element
// - static: value from config
export type ExtractorType = 'urlParam' | 'selector' | 'attribute' | 'static';

export type ExtractorConfig = {
  type: ExtractorType;
  value: string;
};

// Frequency Types:
// - perPage: in-memory Set (resets on navigation)
// - perSession: sessionStorage (persists until browser session ends)
// - onceOnLoad: fires once on page load (deprecated, use perPage)
export type FrequencyType = 'perPage' | 'perSession' | 'onceOnLoad';

// Category options for the dropdown
export const CONVERSION_CATEGORIES = [
  { value: 'pagevisit', label: 'Page Visit' },
  { value: 'leads', label: 'Leads' },
  { value: 'checkout', label: 'Checkout' },
  { value: 'activity', label: 'Activity' },
] as const;

// Trigger type options for the dropdown
export const TRIGGER_TYPES = [
  { value: 'page_load', label: 'Page Load' },
  { value: 'url_match', label: 'URL Match (Regex)' },
  { value: 'url_contains', label: 'URL Contains' },
  { value: 'click', label: 'Click' },
  { value: 'form_submit', label: 'Form Submit' },
] as const;

// Frequency options for the dropdown
export const FREQUENCY_OPTIONS = [
  { value: 'perPage', label: 'Per page visit' },
  { value: 'perSession', label: 'Per session' },
] as const;

// Extractor type options for the dropdown
export const EXTRACTOR_TYPES = [
  { value: 'urlParam', label: 'URL Parameter' },
  { value: 'selector', label: 'DOM Selector' },
  { value: 'attribute', label: 'Element Attribute' },
  { value: 'static', label: 'Static Value' },
] as const;
